<template>
  <div :class="['pc__wrapper', { 'product-card-2026-wrapper': newProductCard }]">
    <!-- Image section -->
    <a
      :href="`/products/${product.handle}?color=${selectedColor}`"
      :class="['pc__images', { OOS: showOOSOverlay }]"
      :data-stio-gtm-click-id="gtmTrackId != '' ? gtmTrackId : null"
      :data-ctl-click="isTrackClickEvent"
      tabindex="0"
      :aria-label="'View product ' + product.title"
    >
      <div v-if="showOOSOverlay" class="pc__oos-badge" :aria-hidden="true">SOLD OUT</div>
      <div v-if="badge">
        <span
          v-if="badge.type === 'Callout'"
          :style="`--bg-color:${badge.bgColor}`"
          :class="[
            'pc__badge',
            {
              color: badge.bgColor.toLowerCase() != '#ffffff',
              'pc__badge--new': badge.tag.toLowerCase().replace('callout: ', '').trim() === 'new'
            }
          ]"
          :aria-hidden="true"
          >{{ badge.tag.toLowerCase().replace('callout: ', '') }}</span
        >
        <ImageField
          v-else
          class="pc__sticker-img"
          :obj="{ url: badge.image, alt: 'Callout', width: 112, height: 112 }"
          :isLoadingLazy="isLoadingLazy"
        />
      </div>
      <ProductImage
        :enableImageCarousel="enableImageCarousel"
        :useLifestyleImage="useLifestyleImage"
        :selectedColor="selectedColor"
        :product="product"
        :isLoadingLazy="isLoadingLazy"
      />
      <button
        data-stio-gtm-id="Quickview CTA"
        v-if="quickview && !newProductCard"
        class="pc__quickview-btn"
        @click.prevent="toggleQuickView"
        :aria-label="'Open quick view for ' + product.title"
      >
        Quickview
      </button>
      <WishlistCTA
        v-if="showWishlistIcon"
        :product="product"
        :selectedVariantDetails="{ Color: selectedColor }"
        :firstOptionName="firstKeyName"
        :showOptions="true"
        :new-product-card="newProductCard"
        customClass="product-card_wishlisticon"
      />
    </a>

    <!-- Info section -->
    <div :class="['pc__info', { 'pc__info--secondTurn': isSecondTurn }]">
      <div class="pc__info-inner">
        <Swatch
          v-if="!isSecondTurn && selectedColor"
          :colorOptions="renderColorOptions"
          :selectedColor="selectedColor"
          :isLoadingLazy="isLoadingLazy"
          :new-product-card="newProductCard"
          :windowWidth="windowWidth"
          @color-change="updateSelectedColor"
        />
        <div :class="['pc__product-title', { 'pc__product-title--margin': !selectedColor }]" role="heading" aria-level="2">
          {{ product.title }}
        </div>
        <div v-if="badge && badge.type === 'Image'" class="pc__sticker">
          {{ badge.tag.toLowerCase().replace('callout: ', '') }}
        </div>
        <div
          v-if="enableReviews && !newProductCard"
          :class="['pc__reviews', { loading: loadMoreProducts }]"
          tabindex="-1"
        >
          <div
            v-if="!isSecondTurn"
            class="yotpo-widget-instance"
            data-yotpo-instance-id="498880"
            :data-yotpo-product-id="product.id"
          ></div>
        </div>
        <div class="pc__price-wrapper">
          <template v-if="newProductCard">
            <div
              v-if="comparePrice > price"
              class="pc__compare-price"
              :aria-label="'Original price: ' + formatPrice(comparePrice)"
            >
              {{ formatPrice(comparePrice) }}
            </div>
            <div :class="['pc__price', { bold: comparePrice > price }]" :aria-label="'Price: ' + formatPrice(price)">
              <span v-if="isSecondTurn" aria-hidden="true">Used</span> {{ formatPrice(price) }}
            </div>
            <PriceSaveText
              v-if="showDiscountBadge"
              :compare-price="comparePrice"
              :current-price="price"
              :discount-percentage="maxVariantDiscountPercentage"
              :label-prefix="hasMultipleDiscountLevels ? 'Up To ' : ''"
              custom-class="pc__price-save-text"
            />
          </template>
          <template v-else>
            <div :class="['pc__price', { bold: comparePrice > price }]" :aria-label="'Price: ' + formatPrice(price)">
              <span v-if="isSecondTurn" aria-hidden="true">Used</span> {{ formatPrice(price) }}
            </div>
            <div v-if="isPriceRange && customerDiscountVal <= 0" class="pc__price-range">{{ isPriceRange }}</div>
            <div
              v-else-if="comparePrice > price"
              class="pc__compare-price"
              :aria-label="'Original price: ' + formatPrice(comparePrice)"
            >
              {{ formatPrice(comparePrice) }}
            </div>
          </template>
        </div>
        <div
          v-if="enableReviews && newProductCard"
          :class="['pc__reviews', { loading: loadMoreProducts }]"
          tabindex="-1"
        >
          <div
            v-if="!isSecondTurn"
            class="yotpo-widget-instance"
            data-yotpo-instance-id="498880"
            :data-yotpo-product-id="product.id"
          ></div>
        </div>
        <button
          v-if="quickview && newProductCard"
          class="pc__quickview-btn-2026"
          @click.prevent="toggleQuickView"
          :aria-label="'Open quick view for ' + product.title"
        >
          Quick View
        </button>
      </div>
      <CompareButton
        v-if="showCompare && product.compare_data.length"
        :productId="product.id"
        :compareProducts="compareProducts"
        :maxCompareItems="windowWidth > 1023 ? 4 : 3"
        :windowWidth="windowWidth"
        @compare-change="onCompareClick"
      />
      <div
        v-if="quickviewBtn"
        class="pc__qv-btn"
        @click.prevent="toggleQuickView"
        tabindex="0"
        :aria-label="'Open quick view for ' + product.title"
      >
        Add to Cart
      </div>
    </div>
  </div>
</template>

<script setup>
  /** ProductCardTypeA is used in PLP, Homepage swiper/slider, Recently Viewed */
  import { computed, toRefs, ref, onBeforeMount, reactive, watch } from 'vue';
  import ImageField from '../image.vue';
  import useWindowResize from '../../../utils/use-window-resize';
  import WishlistCTA from '../wishlist-cta.vue';
  import Swatch from './product-swatch.vue';
  import CompareButton from './compare-button.vue';
  import ProductImage from './product-image.vue';
  import PriceSaveText from '../price-save-text.vue';
  import productCardProps from './product-card-props';

  const props = defineProps({
    isSecondTurn: productCardProps.isSecondTurn,
    loadMoreProducts: productCardProps.loadMoreProducts,
    product: productCardProps.product,
    callouts: productCardProps.callouts,
    quickview: productCardProps.quickview,
    quickviewBtn: productCardProps.quickviewBtn,
    showOOSbadge: productCardProps.showOOSbadge,
    gtmTrackId: productCardProps.gtmTrackId,
    isLoadingLazy: productCardProps.isLoadingLazy,
    isTrackClickEvent: productCardProps.isTrackClickEvent,
    enableImageCarousel: productCardProps.enableImageCarousel,
    useLifestyleImage: productCardProps.useLifestyleImage,
    showDiscountBadge: productCardProps.showDiscountBadge,
    enableReviews: productCardProps.enableReviews,
    showCompare: productCardProps.showCompare,
    compareProducts: productCardProps.compareProducts,
    selectedColorValue: productCardProps.selectedColorValue,
    showWishlistIcon: productCardProps.showWishlistIcon,
    newProductCard: productCardProps.newProductCard,
  });

  const { isSecondTurn, product, callouts, quickview, selectedColorValue } = toRefs(props);

  const firstKeyName = reactive(product.value.options[0].name);
  const selectedColor = ref(null);
  const customerDiscountVal = ref(0);
  const { windowWidth } = useWindowResize();
  const emit = defineEmits(['on-compare-click']);

  onBeforeMount(() => {
    initialSetup();
    getCustomerDiscount();
  });

  watch(product, () => {
    initialSetup();
  });

  const badge = computed(() => {
    if (callouts.value.length) {
      const badgeTag = callouts.value.filter((obj) =>
        product.value.tags.some((value) => value.toLowerCase() === obj.tag.toLowerCase())
      );

      return badgeTag.length ? badgeTag[0] : false;
    } else {
      return false;
    }
  });

  const hasBISTag = computed(() => {
    return product.value.tags.some((tag) => tag.toLowerCase().startsWith('bis:'));
  });

  const showOOSOverlay = computed(() => {
    return !product.value.available && props.showOOSbadge && !hasBISTag.value;
  });

  const activeColorData = computed(() => {
    return selectedColor.value && renderColorOptions.value[selectedColor.value];
  });

  const price = computed(() => {
    if (activeColorData.value && activeColorData.value.comparePrice > 0) {
      const { comparePrice, price } = activeColorData.value;

      if (comparePrice * (1 - customerDiscountVal.value) < price) {
        return comparePrice * (1 - customerDiscountVal.value);
      } else {
        return price;
      }
    } else {
      return (product.value.price / 100) * (1 - customerDiscountVal.value);
    }
  });

  const comparePrice = computed(() => {
    if (activeColorData.value) {
      if (activeColorData.value.comparePrice > 0) {
        return activeColorData.value.comparePrice;
      } else {
        return activeColorData.value.price;
      }
    } else {
      return product.value.compare_at_price / 100;
    }
  });

  const variantsForDiscount = computed(() => {
    const variants = product.value.variants.filter((variant) => variant.available);

    return variants.length ? variants : product.value.variants;
  });

  const variantDiscountLevels = computed(() => {
    return variantsForDiscount.value.map((variant) => {
      const variantPrice = variant.price / 100;
      const variantComparePrice = variant.compare_at_price / 100;
      const displayComparePrice = variantComparePrice > 0 ? variantComparePrice : variantPrice;
      const displayPrice = getDiscountedPrice(variantPrice, variantComparePrice);
      const percentage = displayComparePrice > 0 && displayPrice < displayComparePrice
        ? ((displayComparePrice - displayPrice) / displayComparePrice) * 100
        : 0;

      return {
        price: Math.round(displayPrice * 100),
        percentage: Math.round(percentage),
      };
    });
  });

  const variantDiscounts = computed(() => {
    return variantDiscountLevels.value.filter(({ percentage }) => percentage > 0);
  });

  const maxVariantDiscountPercentage = computed(() => {
    return variantDiscounts.value.reduce((maxPercentage, { percentage }) => {
      return percentage > maxPercentage ? percentage : maxPercentage;
    }, 0);
  });

  const hasMultipleDiscountLevels = computed(() => {
    const discountLevels = new Set(
      variantDiscountLevels.value.map(({ price, percentage }) => `${price}:${percentage}`),
    );

    return discountLevels.size > 1;
  });

  const isPriceRange = computed(() => {
    const { min_price, max_price, variants } = product.value;

    if (min_price == max_price) return false;

    const availableVariants = variants.filter(variant => variant.available);
    
    // Early return if no available variants
    if (!availableVariants.length) return false;

    const hasMinPrice = availableVariants.some(variant => variant.price === min_price);
    const hasMaxPrice = availableVariants.some(variant => variant.price === max_price);

    // Early return if either min or max price is not available
    if (!hasMinPrice || !hasMaxPrice) return false;

    return `(${formatPrice(min_price / 100)} - ${formatPrice(max_price / 100)})`;
  });

  const renderColorOptions = computed(() => {
    const { variants, tags, images, available } = product.value;

    const firstLevelKeys = variants.reduce((set, variant) => {
      const property = variant.option1;
      const setItem = (set[property] = set[property] || { items: [] });
      setItem.items.push(variant);
      if (variant.available) setItem.items.available = true;
      return set;
    }, {});

    Object.entries(firstLevelKeys).forEach(([key, value]) => {
      const isAvailable = value.items.available;
      const bisData = tags.filter((tag) => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`));

      if (isAvailable || bisData.length || !available) {
        const imagePattern = new RegExp(`^\\s*${key.toLowerCase()}\\s*swatch\\s*$`);
        value.image = images.filter((image) => {
          const alt = image.alt.toLowerCase();
          return alt && imagePattern.test(alt);
        })[0];

        value.price = Number(value.items[0].price / 100);
        value.comparePrice = Number(value.items[0].compare_at_price / 100);
      } else {
        delete firstLevelKeys[key];
      }
    });
    return firstLevelKeys;
  });

  const initialSetup = () => {
    if (renderColorOptions.value && firstKeyName === 'Color') {
      selectedColor.value = selectedColorValue.value || Object.keys(renderColorOptions.value)[0];
    }
  };

  const updateSelectedColor = (color) => {
    selectedColor.value = color;
  };

  const getDiscountedPrice = (variantPrice, variantComparePrice) => {
    if (variantComparePrice > 0) {
      if (variantComparePrice * (1 - customerDiscountVal.value) < variantPrice) {
        return variantComparePrice * (1 - customerDiscountVal.value);
      }

      return variantPrice;
    }

    return variantPrice * (1 - customerDiscountVal.value);
  };

  const getCustomerDiscount = () => {
    customerDiscountVal.value = window.customerDiscount(product.value.tags);
  };

  const toggleQuickView = () => {
    const drawerName = props.newProductCard ? 'QuickView2026' : 'QuickView';
    window.toggleDrawer(true, drawerName, { product: product.value, selectedColor: selectedColor.value });
  };

  const formatPrice = (value) => {
    return window.formattedPrice(value);
  };

  const onCompareClick = (checked) => {
    product.value.Color = selectedColor.value;
    emit('on-compare-click', checked, product.value);
  };
</script>
